import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PROJECTS } from "../data/content";

const COLOR_HEX = {
  "four-red": "#C85A5A",
  "four-yellow": "#E3B14B",
  "four-green": "#859368",
  "four-blue": "#789BBD",
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, isRTL } = useLang();
  const project = PROJECTS.find((p) => p.slug === slug);
  const visibleProjects = PROJECTS.filter((p) => !p.hidden);
  const idx = visibleProjects.findIndex((p) => p.slug === slug);
  const next = idx >= 0 ? visibleProjects[(idx + 1) % visibleProjects.length] : visibleProjects[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  const accent = COLOR_HEX[project.color];
  const ArrowIcon = isRTL ? ArrowUpRight : ArrowLeft;

  return (
    <main className="pt-32 pb-24 paper-grain min-h-screen" data-testid={`project-detail-${slug}`}>
      <article className="max-w-4xl mx-auto px-6 lg:px-12">
        <Link
          to="/#projects"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-parchment-dusk hover:text-parchment transition-colors mb-12"
          data-testid="back-link"
        >
          <ArrowIcon size={16} style={isRTL ? { transform: "scaleX(-1)" } : undefined} />
          {t("back to projects", "חזרה לפרויקטים")}
        </Link>

        <header className="mb-12">
          <p className="chapter-num mb-4" style={{ color: accent }}>
            {t(`Project 0${idx + 1}`, `פרויקט 0${idx + 1}`)}
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-parchment leading-[0.95] mb-6">
            {t(project.name.en, project.name.he)}
          </h1>
          <p
            className="font-serif italic text-xl sm:text-2xl mb-4 leading-snug"
            style={{ color: accent }}
          >
            {t(project.tagline.en, project.tagline.he)}
          </p>
          <div className="brush-divider" />
        </header>

        <div
          className="ink-circle relative mx-auto mb-16"
          style={{ width: "min(420px, 80vw)", height: "min(420px, 80vw)" }}
        >
          <img
            src={project.image}
            alt={t(project.name.en, project.name.he)}
            className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)] object-cover rounded-full"
          />
        </div>

        <div className="space-y-6 max-w-2xl mx-auto">
          {project.body[isRTL ? "he" : "en"].map((para, i) => (
            <p
              key={i}
              className={`text-lg leading-relaxed text-parchment-warm/90 font-light ${
                i === 0 ? "drop-cap" : ""
              }`}
              data-testid={`project-body-${i}`}
            >
              {para.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
                seg.startsWith("**") && seg.endsWith("**") ? (
                  <strong
                    key={j}
                    className="font-semibold text-parchment"
                  >
                    {seg.slice(2, -2)}
                  </strong>
                ) : (
                  seg
                )
              )}
            </p>
          ))}
        </div>

        {(project.links.length > 0 || project.comingSoon) && (
          <div className="mt-16 max-w-2xl mx-auto">
            <p className="chapter-num mb-4">{t("explore", "חקור")}</p>
            <div className="brush-divider" />
            <div className="flex flex-wrap gap-3 mt-6">
              {project.comingSoon ? (
                <span
                  className="inline-flex items-center gap-2 px-5 py-3 border border-dashed border-parchment-faint text-parchment-dusk text-sm tracking-wide italic"
                  style={{ color: accent, borderColor: `${accent}66` }}
                  data-testid="project-coming-soon"
                >
                  {t("Coming soon", "בקרוב")}
                </span>
              ) : (
                project.links.map((l) => {
                  const label = typeof l.label === "string" ? l.label : t(l.label.en, l.label.he);
                  return (
                    <a
                      key={l.url}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-3 border border-parchment-faint hover:border-four-yellow text-parchment hover:text-four-yellow text-sm tracking-wide transition-all duration-500"
                      data-testid={`project-link-${label}`}
                    >
                      {label}
                      <ArrowUpRight size={14} />
                    </a>
                  );
                })
              )}
            </div>
          </div>
        )}

        <div className="mt-24 pt-12 border-t border-parchment-faint max-w-2xl mx-auto">
          <p className="chapter-num mb-3">{t("next project", "הפרויקט הבא")}</p>
          <Link
            to={`/projects/${next.slug}`}
            className="group flex items-center justify-between"
            data-testid="next-project"
          >
            <h3 className="font-serif text-3xl lg:text-4xl text-parchment group-hover:text-four-yellow transition-colors">
              {t(next.name.en, next.name.he)}
            </h3>
            <ArrowUpRight
              size={28}
              className="text-parchment-dusk group-hover:text-four-yellow group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
              style={isRTL ? { transform: "scaleX(-1)" } : undefined}
            />
          </Link>
        </div>
      </article>
    </main>
  );
}
